import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "app/components/Modals/SimpleModal";
import Button from "app/storybookComponents/Button";
import { toast } from "react-toastify";
import { useAppDispatch } from "utils/redux/hooks";
import { INTEGRATION_LOOKUP } from "../../containers/AdminConsole/constants";
import {
  disconnectIntegration,
  fetchIntegrationStatus,
} from "app/containers/AdminConsole/slice";
import { useState } from "react";

interface Props {
  show: boolean;
  hideModal: () => void;
  platform: string;
  onDisconnected?: () => void; // optional callback to refresh status
}

export default function IntegrationDisconnectModal({
  show,
  hideModal,
  platform,
  onDisconnected,
}: Readonly<Props>) {
  const dispatch = useAppDispatch();
  const details = INTEGRATION_LOOKUP[platform];
  const [loading, setLoading] = useState(false);

  const handleDisconnect = async () => {
    setLoading(true);

    try {
      const result = await dispatch(disconnectIntegration(platform)).unwrap();

      if (result.status) {
        toast.success(`${details.name} disconnected successfully.`);

        hideModal();

        // Allow parent to refresh UI
        if (onDisconnected) onDisconnected();

        // Refresh status in redux
        dispatch(fetchIntegrationStatus(platform));
      } else {
        toast.error(result.message || `Failed to disconnect ${details.name}.`);
      }
    } catch (err: any) {
      toast.error(`Failed to disconnect ${details.name}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={hideModal}
      size="lg"
      title={`Disconnect ${details.name}`}
      className="integration-disconnect-modal"
    >
      <div className="text-center py-4 px-3">
        {/* Icon */}
        <div
          className="w-[70px] h-[70px] rounded-xl flex justify-center items-center mx-auto mb-[18px]"
          style={{ backgroundColor: details.color }}
        >
          <FontAwesomeIcon
            icon={details.icon}
            className="text-white text-[36px]"
          />
        </div>

        {/* Text */}
        <h5 className="font-semibold">
          Disconnect {details.name} Integration?
        </h5>
        <p className="text-gray-500 text-[0.95rem] my-2.5 mb-6">
          This will permanently disconnect Coach Bo from your {details.name}{" "}
          workspace. You can reconnect anytime later.
        </p>

        {/* Buttons */}
        <div className="flex justify-center gap-3 mt-2">
          <Button
            variant="secondary-blue"
            onClick={hideModal}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDisconnect}
            disabled={loading}
          >
            {loading ? "Disconnecting..." : "Disconnect"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
